/**
 * ChartService — builds Chart.js config objects for every supported figure
 * type in the plugin. All statistical heavy-lifting lives in StatsService;
 * this file focuses on data prep + Chart.js config assembly.
 *
 * Chart.js 4.x is required (available as window.Chart or this.$Chart in
 * components).
 */

import Stats from './StatsService.js'

// --- palettes & defaults ---------------------------------------------------

const DEFAULT_PALETTE = [
  '#0B5ED7', '#DC3545', '#198754', '#6610F2', '#FD7E14',
  '#20C997', '#D63384', '#6C757D', '#FFC107', '#0DCAF0'
]

const palette = (i) => DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]

const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex
  const h = hex.slice(1)
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// --- data access -----------------------------------------------------------

/**
 * Extract tabular rows from an Every.Farm dataset object.
 * Prefers GeoJSON `features[].properties`, falls back to `data[]`.
 */
export function extractRows (dataset) {
  if (!dataset) return []
  if (dataset.geoJson && Array.isArray(dataset.geoJson.features) && dataset.geoJson.features.length) {
    return dataset.geoJson.features.map(f => f && f.properties ? f.properties : {})
  }
  if (Array.isArray(dataset.data)) return dataset.data
  return []
}

/**
 * Distinct numeric columns present on the dataset (friendly field names).
 * Used by configurators to populate axis pickers.
 */
export function numericColumns (dataset) {
  const rows = extractRows(dataset)
  if (!rows.length) return []
  const keys = new Set()
  for (const row of rows.slice(0, 200)) {
    Object.keys(row || {}).forEach(k => keys.add(k))
  }
  const numeric = []
  for (const k of keys) {
    const sample = rows.slice(0, 50).map(r => r[k])
    const type = Stats.inferColumnType(sample)
    if (type === 'number') numeric.push(k)
  }
  return numeric.sort()
}

/**
 * All column names (any type) present on the dataset.
 */
export function allColumns (dataset) {
  const rows = extractRows(dataset)
  if (!rows.length) return []
  const keys = new Set()
  for (const row of rows.slice(0, 200)) {
    Object.keys(row || {}).forEach(k => keys.add(k))
  }
  return Array.from(keys).sort()
}

export function columnType (dataset, column) {
  const rows = extractRows(dataset)
  const sample = rows.slice(0, 50).map(r => r[column])
  return Stats.inferColumnType(sample)
}

// --- chart type registry ---------------------------------------------------

export const CHART_TYPES = [
  { id: 'scatter',     name: 'Scatter',     icon: 'fas fa-braille',       description: 'Two numeric variables plotted against each other. Supports regression + reference lines.' },
  { id: 'line',        name: 'Line',        icon: 'fas fa-chart-line',    description: 'Values across an ordered axis — good for trends or time series.' },
  { id: 'timeseries',  name: 'Time Series', icon: 'far fa-calendar-alt',  description: 'Line chart over a datetime axis with automatic scaling.' },
  { id: 'bar',         name: 'Bar',         icon: 'fas fa-chart-bar',     description: 'Compare a numeric value across categories.' },
  { id: 'grouped_bar', name: 'Grouped Bar', icon: 'fas fa-chart-column',  description: 'Multiple numeric values side-by-side per category.' },
  { id: 'histogram',   name: 'Histogram',   icon: 'fas fa-chart-area',    description: 'Distribution of a single numeric variable with optional KDE.' },
  { id: 'box',         name: 'Box Plot',    icon: 'fas fa-grip-lines',    description: 'Five-number summary per group (quartiles + outliers).' },
  { id: 'density',     name: 'Density',     icon: 'fas fa-wave-square',   description: 'Smooth kernel density estimate of a numeric variable.' },
  { id: 'correlation', name: 'Correlation', icon: 'fas fa-th',            description: 'Pearson correlation matrix across all numeric columns.' },
  { id: 'pie',         name: 'Pie / Donut', icon: 'fas fa-chart-pie',     description: 'Share of categories for a single field.' }
]

// --- chart builders --------------------------------------------------------

function baseOptions (config) {
  const isCorrelation = config.chartKind === 'correlation'
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      title: {
        display: !!config.title,
        text: config.title || '',
        font: { size: 16, weight: 600 }
      },
      legend: {
        display: config.showLegend !== false && !isCorrelation
      },
      tooltip: {
        backgroundColor: config.tooltipBackgroundColor || 'rgba(15,23,42,0.92)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
        cornerRadius: 4
      }
    },
    scales: {
      x: {
        title: { display: !!config.xLabel, text: config.xLabel || '' },
        grid: { display: config.showGrid !== false }
      },
      y: {
        title: { display: !!config.yLabel, text: config.yLabel || '' },
        grid: { display: config.showGrid !== false }
      }
    }
  }
}

function buildScatter (dataset, config, secondDataset = null) {
  const xKey = config.xAxis
  const yKey = config.yAxis
  if (!xKey || !yKey) throw new Error('Scatter plot requires X and Y axes')

  const rows1 = extractRows(dataset)
  const series1 = rows1
    .map(r => ({ x: Number(r[xKey]), y: Number(r[yKey]) }))
    .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y))

  const datasets = [{
    label: dataset.name || 'Dataset',
    data: series1,
    backgroundColor: withAlpha(config.pointColor || palette(0), 0.65),
    borderColor: config.pointColor || palette(0),
    pointRadius: config.pointSize || 4,
    pointHoverRadius: (config.pointSize || 4) + 2
  }]

  // Second dataset overlay
  let series2
  if (secondDataset) {
    const rows2 = extractRows(secondDataset)
    series2 = rows2
      .map(r => ({ x: Number(r[xKey]), y: Number(r[yKey]) }))
      .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y))
    datasets.push({
      label: secondDataset.name || 'Dataset 2',
      data: series2,
      backgroundColor: withAlpha(palette(1), 0.65),
      borderColor: palette(1),
      pointRadius: config.pointSize || 4,
      pointHoverRadius: (config.pointSize || 4) + 2
    })
  }

  const stats = { series1: null, series2: null, regression: null }
  if (config.showRegression !== false && series1.length >= 2) {
    const reg = Stats.linearRegression(series1.map(p => p.x), series1.map(p => p.y))
    if (reg) {
      const xs = series1.map(p => p.x)
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs)
      const line = [
        { x: minX, y: reg.slope * minX + reg.intercept },
        { x: maxX, y: reg.slope * maxX + reg.intercept }
      ]
      datasets.push({
        type: 'line',
        label: `Regression (R² = ${Stats.fmt(reg.r2, 3)})`,
        data: line,
        borderColor: config.regressionColor || '#dc3545',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        fill: false,
        tension: 0,
        showLine: true,
        order: 10
      })
      stats.regression = reg
    }
  }

  stats.series1 = Stats.summarize(series1.map(p => p.y))
  if (series2) stats.series2 = Stats.summarize(series2.map(p => p.y))

  const options = baseOptions({ ...config, chartKind: 'scatter' })
  options.scales.x.type = 'linear'
  options.scales.y.type = 'linear'

  return {
    type: 'scatter',
    data: { datasets },
    options,
    stats,
    meta: { kind: 'scatter' }
  }
}

function buildLine (dataset, config, secondDataset = null) {
  const xKey = config.xAxis
  const yKey = config.yAxis
  if (!xKey || !yKey) throw new Error('Line chart requires X and Y axes')

  const prepSeries = (ds) => {
    const rows = extractRows(ds)
    return rows
      .map(r => ({ x: r[xKey], y: Number(r[yKey]), _sortKey: Number(r[xKey]) || new Date(r[xKey]).getTime() }))
      .filter(p => Number.isFinite(p.y))
      .sort((a, b) => (a._sortKey || 0) - (b._sortKey || 0))
  }

  const s1 = prepSeries(dataset)
  const datasets = [{
    label: dataset.name || 'Dataset',
    data: s1.map(p => ({ x: p.x, y: p.y })),
    borderColor: config.lineColor || palette(0),
    backgroundColor: withAlpha(config.lineColor || palette(0), 0.15),
    borderWidth: config.lineWidth || 2,
    tension: config.tension || 0.1,
    pointRadius: config.showPoints ? (config.pointSize || 3) : 0,
    fill: !!config.fillArea
  }]

  let s2
  if (secondDataset) {
    s2 = prepSeries(secondDataset)
    datasets.push({
      label: secondDataset.name || 'Dataset 2',
      data: s2.map(p => ({ x: p.x, y: p.y })),
      borderColor: palette(1),
      backgroundColor: withAlpha(palette(1), 0.15),
      borderWidth: config.lineWidth || 2,
      tension: config.tension || 0.1,
      pointRadius: config.showPoints ? (config.pointSize || 3) : 0,
      fill: !!config.fillArea
    })
  }

  const options = baseOptions({ ...config, chartKind: 'line' })
  return {
    type: 'line',
    data: { datasets },
    options,
    stats: { series1: Stats.summarize(s1.map(p => p.y)), series2: s2 ? Stats.summarize(s2.map(p => p.y)) : null },
    meta: { kind: 'line' }
  }
}

function buildTimeSeries (dataset, config, secondDataset = null) {
  const result = buildLine(dataset, config, secondDataset)
  // Coerce x to Date timestamps for time scale — Chart.js handles Date or ms.
  result.data.datasets.forEach(ds => {
    ds.data = ds.data.map(p => {
      const t = p.x instanceof Date ? p.x : new Date(p.x)
      return { x: Number.isNaN(t.getTime()) ? p.x : t, y: p.y }
    })
  })
  result.options.scales.x.type = 'time'
  result.options.scales.x.time = { tooltipFormat: 'PP' }
  result.meta.kind = 'timeseries'
  return result
}

function buildBar (dataset, config, secondDataset = null) {
  const xKey = config.xAxis
  const yKey = config.yAxis
  if (!xKey || !yKey) throw new Error('Bar chart requires category and value axes')

  const buildGroups = (ds) => {
    const rows = extractRows(ds)
    const groups = {}
    for (const r of rows) {
      const cat = r[xKey]
      const val = Number(r[yKey])
      if (cat === undefined || cat === null || cat === '' || !Number.isFinite(val)) continue
      const label = String(cat)
      if (!groups[label]) groups[label] = []
      groups[label].push(val)
    }
    const reducer = config.aggregation || 'mean'
    const labels = Object.keys(groups).sort()
    const values = labels.map(l => {
      const arr = groups[l]
      if (reducer === 'sum') return arr.reduce((s, v) => s + v, 0)
      if (reducer === 'count') return arr.length
      if (reducer === 'max') return Math.max(...arr)
      if (reducer === 'min') return Math.min(...arr)
      if (reducer === 'median') return Stats.summarize(arr).median
      return Stats.summarize(arr).mean
    })
    return { labels, values }
  }

  const g1 = buildGroups(dataset)
  const datasets = [{
    label: `${dataset.name || 'Dataset'} (${config.aggregation || 'mean'})`,
    data: g1.values,
    backgroundColor: withAlpha(config.barColor || palette(0), 0.85),
    borderColor: config.barColor || palette(0),
    borderWidth: 1
  }]

  let labels = g1.labels
  if (secondDataset) {
    const g2 = buildGroups(secondDataset)
    const merged = Array.from(new Set([...g1.labels, ...g2.labels])).sort()
    const align = (g) => merged.map(l => {
      const i = g.labels.indexOf(l)
      return i >= 0 ? g.values[i] : null
    })
    datasets[0].data = align(g1)
    datasets.push({
      label: `${secondDataset.name || 'Dataset 2'} (${config.aggregation || 'mean'})`,
      data: align(g2),
      backgroundColor: withAlpha(palette(1), 0.85),
      borderColor: palette(1),
      borderWidth: 1
    })
    labels = merged
  }

  const options = baseOptions({ ...config, chartKind: 'bar' })
  return {
    type: 'bar',
    data: { labels, datasets },
    options,
    stats: { groups: g1 },
    meta: { kind: 'bar' }
  }
}

function buildGroupedBar (dataset, config) {
  const xKey = config.xAxis
  const yFields = Array.isArray(config.yFields) && config.yFields.length
    ? config.yFields
    : (config.yAxis ? [config.yAxis] : [])
  if (!xKey || !yFields.length) throw new Error('Grouped bar requires X axis and at least one Y field')

  const rows = extractRows(dataset)
  const groups = {}
  for (const r of rows) {
    const cat = r[xKey]
    if (cat === undefined || cat === null || cat === '') continue
    const label = String(cat)
    if (!groups[label]) groups[label] = {}
    for (const f of yFields) {
      const v = Number(r[f])
      if (Number.isFinite(v)) {
        if (!groups[label][f]) groups[label][f] = []
        groups[label][f].push(v)
      }
    }
  }
  const labels = Object.keys(groups).sort()
  const reducer = config.aggregation || 'mean'
  const reduce = (arr) => {
    if (!arr || !arr.length) return null
    if (reducer === 'sum') return arr.reduce((s, v) => s + v, 0)
    if (reducer === 'count') return arr.length
    if (reducer === 'max') return Math.max(...arr)
    if (reducer === 'min') return Math.min(...arr)
    if (reducer === 'median') return Stats.summarize(arr).median
    return Stats.summarize(arr).mean
  }
  const datasets = yFields.map((f, i) => ({
    label: f,
    data: labels.map(l => reduce(groups[l][f])),
    backgroundColor: withAlpha(palette(i), 0.85),
    borderColor: palette(i),
    borderWidth: 1
  }))
  const options = baseOptions({ ...config, chartKind: 'grouped_bar' })
  return {
    type: 'bar',
    data: { labels, datasets },
    options,
    stats: {},
    meta: { kind: 'grouped_bar' }
  }
}

function buildHistogram (dataset, config) {
  const key = config.xAxis
  if (!key) throw new Error('Histogram requires a field')

  const rows = extractRows(dataset)
  const values = rows.map(r => Number(r[key])).filter(v => Number.isFinite(v))
  const hist = Stats.histogram(values, config.bins || null)
  const summary = Stats.summarize(values)

  const datasets = [{
    label: 'Frequency',
    data: hist.bins.map(b => b.count),
    backgroundColor: withAlpha(config.barColor || palette(0), 0.85),
    borderColor: config.barColor || palette(0),
    borderWidth: 1,
    categoryPercentage: 1.0,
    barPercentage: 1.0
  }]

  if (config.showDensity) {
    const kde = Stats.kernelDensity(values)
    if (kde.length) {
      const maxKde = Math.max(...kde.map(p => p.y))
      const maxCount = Math.max(...hist.bins.map(b => b.count))
      const scale = maxKde > 0 ? maxCount / maxKde : 1
      datasets.push({
        type: 'line',
        label: 'Density',
        data: kde.map(p => ({ x: p.x, y: p.y * scale })),
        borderColor: '#dc3545',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        xAxisID: 'xLinear',
        order: 10
      })
    }
  }

  const options = baseOptions({ ...config, chartKind: 'histogram', yLabel: config.yLabel || 'Count' })
  options.scales.x = {
    ...options.scales.x,
    title: { display: true, text: config.xLabel || key }
  }
  const labels = hist.bins.map(b => Stats.fmt(b.midpoint, 3))

  if (config.showDensity) {
    options.scales.xLinear = {
      type: 'linear',
      position: 'bottom',
      display: false,
      min: hist.min,
      max: hist.max
    }
  }

  return {
    type: 'bar',
    data: { labels, datasets },
    options,
    stats: { summary, bins: hist.bins.length },
    meta: { kind: 'histogram' }
  }
}

function buildBox (dataset, config) {
  const valueField = config.yAxis
  const groupField = config.xAxis
  if (!valueField) throw new Error('Box plot requires a value field')
  const rows = extractRows(dataset)

  let groups = {}
  if (groupField) {
    for (const r of rows) {
      const cat = r[groupField]
      const v = Number(r[valueField])
      if (cat === undefined || cat === null || cat === '' || !Number.isFinite(v)) continue
      const label = String(cat)
      if (!groups[label]) groups[label] = []
      groups[label].push(v)
    }
  } else {
    groups = { [valueField]: rows.map(r => Number(r[valueField])).filter(v => Number.isFinite(v)) }
  }
  const labels = Object.keys(groups).sort()
  const stats = labels.map(l => Stats.boxplotStats(groups[l]))

  const iqrData = stats.map(s => s ? [s.q1, s.q3] : [null, null])
  const medianData = stats.map(s => s ? [s.median, s.median] : [null, null])
  const whiskerLowerData = stats.map(s => s ? [s.min, s.q1] : [null, null])
  const whiskerUpperData = stats.map(s => s ? [s.q3, s.max] : [null, null])

  const barColor = config.barColor || palette(0)
  const datasets = [
    {
      label: 'Whisker (min → Q1)',
      data: whiskerLowerData,
      backgroundColor: withAlpha(barColor, 0.2),
      borderColor: barColor,
      borderWidth: 1,
      barPercentage: 0.05
    },
    {
      label: 'IQR (Q1 → Q3)',
      data: iqrData,
      backgroundColor: withAlpha(barColor, 0.6),
      borderColor: barColor,
      borderWidth: 1.5,
      barPercentage: 0.5
    },
    {
      label: 'Whisker (Q3 → max)',
      data: whiskerUpperData,
      backgroundColor: withAlpha(barColor, 0.2),
      borderColor: barColor,
      borderWidth: 1,
      barPercentage: 0.05
    },
    {
      label: 'Median',
      data: medianData,
      backgroundColor: '#000',
      borderColor: '#000',
      borderWidth: 3,
      barPercentage: 0.5
    }
  ]

  const outliers = []
  stats.forEach((s, i) => {
    if (!s) return
    for (const o of s.outliers) outliers.push({ x: labels[i], y: o })
  })
  if (outliers.length) {
    datasets.push({
      type: 'scatter',
      label: 'Outliers',
      data: outliers,
      backgroundColor: '#dc3545',
      borderColor: '#dc3545',
      pointRadius: 3,
      order: 10
    })
  }

  const options = baseOptions({ ...config, chartKind: 'box' })
  return {
    type: 'bar',
    data: { labels, datasets },
    options,
    stats: { groups: labels.map((l, i) => ({ label: l, ...stats[i] })) },
    meta: { kind: 'box' }
  }
}

function buildDensity (dataset, config) {
  const key = config.xAxis
  if (!key) throw new Error('Density plot requires a field')
  const rows = extractRows(dataset)
  const values = rows.map(r => Number(r[key])).filter(v => Number.isFinite(v))
  const kde = Stats.kernelDensity(values, { samples: 200 })
  const summary = Stats.summarize(values)

  const color = config.lineColor || palette(0)
  const datasets = [{
    label: 'Density',
    data: kde,
    borderColor: color,
    backgroundColor: withAlpha(color, 0.2),
    borderWidth: 2,
    pointRadius: 0,
    fill: true,
    tension: 0.3
  }]

  if (summary.mean !== null && kde.length) {
    const ymax = Math.max(...kde.map(p => p.y))
    datasets.push({
      type: 'line',
      label: `Mean (${Stats.fmt(summary.mean, 3)})`,
      data: [{ x: summary.mean, y: 0 }, { x: summary.mean, y: ymax }],
      borderColor: '#dc3545',
      borderDash: [5, 4],
      borderWidth: 1.5,
      pointRadius: 0,
      fill: false
    })
  }

  const options = baseOptions({ ...config, chartKind: 'density', yLabel: config.yLabel || 'Density' })
  options.scales.x.type = 'linear'
  options.scales.x.title = { display: true, text: config.xLabel || key }
  return {
    type: 'line',
    data: { datasets },
    options,
    stats: { summary },
    meta: { kind: 'density' }
  }
}

function buildCorrelation (dataset, config) {
  const cols = config.columns && config.columns.length ? config.columns : numericColumns(dataset).slice(0, 10)
  if (cols.length < 2) throw new Error('Correlation matrix needs at least 2 numeric columns')
  const rows = extractRows(dataset)
  const rvals = {}
  for (const c of cols) rvals[c] = rows.map(r => Number(r[c]))

  const matrix = []
  for (let i = 0; i < cols.length; i++) {
    const row = []
    for (let j = 0; j < cols.length; j++) {
      row.push(Stats.correlation(rvals[cols[i]], rvals[cols[j]]))
    }
    matrix.push(row)
  }

  const labels = []
  const data = []
  const bgColors = []
  for (let i = 0; i < cols.length; i++) {
    for (let j = i + 1; j < cols.length; j++) {
      labels.push(`${cols[i]} × ${cols[j]}`)
      data.push(matrix[i][j])
      const val = matrix[i][j]
      const intensity = Math.min(1, Math.abs(val))
      bgColors.push(val >= 0
        ? `rgba(11,94,215,${0.15 + 0.7 * intensity})`
        : `rgba(220,53,69,${0.15 + 0.7 * intensity})`)
    }
  }

  const datasets = [{
    label: 'Pearson r',
    data,
    backgroundColor: bgColors,
    borderColor: '#495057',
    borderWidth: 0.5
  }]
  const options = baseOptions({ ...config, chartKind: 'correlation', yLabel: config.yLabel || '' })
  options.indexAxis = 'y'
  options.scales.x.min = -1
  options.scales.x.max = 1
  options.scales.x.title = { display: true, text: 'Pearson r (−1 to 1)' }
  return {
    type: 'bar',
    data: { labels, datasets },
    options,
    stats: { matrix, columns: cols },
    meta: { kind: 'correlation' }
  }
}

function buildPie (dataset, config) {
  const key = config.xAxis
  if (!key) throw new Error('Pie/donut requires a category field')
  const rows = extractRows(dataset)
  const groups = {}
  for (const r of rows) {
    const k = r[key]
    if (k === undefined || k === null || k === '') continue
    const label = String(k)
    groups[label] = (groups[label] || 0) + 1
  }
  const labels = Object.keys(groups).sort((a, b) => groups[b] - groups[a])
  const data = labels.map(l => groups[l])
  const colors = labels.map((_, i) => palette(i))

  const options = baseOptions({ ...config, chartKind: 'pie' })
  options.scales = {}
  return {
    type: config.donut ? 'doughnut' : 'pie',
    data: {
      labels,
      datasets: [{
        label: config.xAxis,
        data,
        backgroundColor: colors,
        borderColor: '#fff',
        borderWidth: 2
      }]
    },
    options,
    stats: { total: data.reduce((s, v) => s + v, 0), groups },
    meta: { kind: 'pie' }
  }
}

// --- public API ------------------------------------------------------------

export async function generateChart (dataset, figureType, config = {}, secondDataset = null) {
  const id = typeof figureType === 'string' ? figureType : figureType && figureType.id
  if (!id) throw new Error('Missing figure type')
  config = { ...config, chartKind: id }

  switch (id) {
    case 'scatter':     return buildScatter(dataset, config, secondDataset)
    case 'line':        return buildLine(dataset, config, secondDataset)
    case 'timeseries':  return buildTimeSeries(dataset, config, secondDataset)
    case 'bar':         return buildBar(dataset, config, secondDataset)
    case 'grouped_bar': return buildGroupedBar(dataset, config)
    case 'histogram':   return buildHistogram(dataset, config)
    case 'box':         return buildBox(dataset, config)
    case 'density':     return buildDensity(dataset, config)
    case 'correlation': return buildCorrelation(dataset, config)
    case 'pie':         return buildPie(dataset, config)
    default: throw new Error(`Unsupported chart type: ${id}`)
  }
}

// --- export helpers --------------------------------------------------------

/**
 * Download the rendered chart as PNG.
 * - scale: 1 | 2 | 3 — DPI multiplier for high-resolution export
 * - backgroundColor: fill behind the chart (default white)
 */
export function downloadChartPng (chart, filename, opts = {}) {
  if (!chart || !chart.canvas) return
  const scale = opts.scale || 2
  const src = chart.canvas
  const canvas = document.createElement('canvas')
  canvas.width = src.width * scale
  canvas.height = src.height * scale
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = opts.backgroundColor || '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(src, 0, 0, canvas.width, canvas.height)
  triggerDownload(canvas.toDataURL('image/png'), `${filename || 'figure'}.png`)
}

export function downloadChartData (chartConfig, filename) {
  const rows = chartDataToRows(chartConfig)
  if (!rows.length) return
  const csv = rowsToCsv(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  triggerDownload(URL.createObjectURL(blob), `${filename || 'figure'}-data.csv`)
}

export function downloadChartStats (chartConfig, filename) {
  const stats = chartConfig && chartConfig.stats ? chartConfig.stats : {}
  const rows = []
  if (stats.summary) flattenStatsBlock(rows, 'summary', stats.summary)
  if (stats.series1) flattenStatsBlock(rows, 'series1', stats.series1)
  if (stats.series2) flattenStatsBlock(rows, 'series2', stats.series2)
  if (stats.regression) flattenStatsBlock(rows, 'regression', stats.regression)
  if (stats.groups && Array.isArray(stats.groups)) {
    stats.groups.forEach(g => flattenStatsBlock(rows, `group[${g.label}]`, g))
  }
  if (stats.matrix && stats.columns) {
    stats.columns.forEach((c, i) => {
      stats.columns.forEach((c2, j) => {
        rows.push({ metric: `r(${c},${c2})`, value: stats.matrix[i][j] })
      })
    })
  }
  if (!rows.length) return
  const csv = rowsToCsv(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  triggerDownload(URL.createObjectURL(blob), `${filename || 'figure'}-stats.csv`)
}

export function downloadChartJson (chartConfig, filename) {
  const cleaned = {
    type: chartConfig.type,
    data: chartConfig.data,
    options: chartConfig.options,
    meta: chartConfig.meta,
    stats: chartConfig.stats
  }
  const blob = new Blob([JSON.stringify(cleaned, null, 2)], { type: 'application/json' })
  triggerDownload(URL.createObjectURL(blob), `${filename || 'figure'}.json`)
}

function triggerDownload (url, filename) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

function chartDataToRows (chartConfig) {
  if (!chartConfig || !chartConfig.data) return []
  const out = []
  const datasets = chartConfig.data.datasets || []
  const labels = chartConfig.data.labels || []
  datasets.forEach(ds => {
    (ds.data || []).forEach((p, i) => {
      const row = { series: ds.label }
      if (typeof p === 'object' && p !== null) {
        row.x = p.x instanceof Date ? p.x.toISOString() : p.x
        row.y = p.y
      } else {
        row.x = labels[i] !== undefined ? labels[i] : i
        row.y = p
      }
      out.push(row)
    })
  })
  return out
}

function flattenStatsBlock (rows, prefix, block) {
  if (!block || typeof block !== 'object') return
  for (const [k, v] of Object.entries(block)) {
    if (typeof v === 'object' && v !== null) continue
    rows.push({ metric: `${prefix}.${k}`, value: v })
  }
}

function rowsToCsv (rows) {
  if (!rows.length) return ''
  const cols = Array.from(rows.reduce((s, r) => {
    Object.keys(r).forEach(k => s.add(k)); return s
  }, new Set()))
  const esc = (v) => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n')
}

// Default export (legacy class-compatible façade)
export default {
  CHART_TYPES,
  generateChart,
  extractRows,
  numericColumns,
  allColumns,
  columnType,
  downloadChartPng,
  downloadChartData,
  downloadChartStats,
  downloadChartJson
}
